<template>
  <div class="smartwatch-configurator">
    <div
      v-if="offerImages.length > 0"
      class="smartwatch-configurator__image"
    >
      <ImageGalleryContainer
        :media-data="offerImages"
        class="smartwatch-configurator__image-gallery"
      />
    </div>
    <div class="smartwatch-configurator__variant">
      <div
        v-if="smartwatchModels.length > 0"
        class="smartwatch-configurator__model-select"
      >
        <SmartwatchSelect
          name="Modell wählen"
          :entries="smartwatchModels"
          @select="(val) => (selectedGroup = val)"
        />
      </div>
      <div
        v-if="smartwatchConnectivity.length > 0"
        class="smartwatch-configurator__connectivity-select"
      >
        <SmartwatchSelect
          name="Anbindung"
          :entries="smartwatchConnectivity"
          @select="(val) => (selectedArticleNumber = val)"
        />
      </div>

      <div
        v-if="smartwatchSize.length > 0"
        class="smartwatch-configurator__size-select"
      >
        <SmartwatchSelect
          name="Gehäuse Größe"
          :entries="smartwatchSize"
          @select="(val) => (selectedArticleNumber = val)"
        />
      </div>

      <div
        v-if="smartwatchColors.length > 0"
        class="smartwatch-configurator__color-select"
      >
        <SmartwatchSelect
          name="Modell Farbe"
          :entries="smartwatchColors"
          @select="(val) => (selectedArticleNumber = val)"
        />
      </div>
    </div>

    <div
      v-if="accessoriesWithOffers"
      class="smartwatch-configurator__additional"
    >
      <AccordionItem @update:isOpen="openAccordion">
        <template #title> Zusätzliches Armband wählen </template>
        <template #content>
          <div class="smartwatch-configurator__additional-list">
            <SmartwatchSelect
              ref="selectAccessoireElement"
              name="Armband"
              :entries="accessoriesList"
              class="smartwatch-configurator__additional-armband"
              @select="selectAccessoire"
            />

            <Heading
              v-if="accessoriesColors.length > 1"
              tag="h4"
              size="sm"
              class="smartwatch-configurator__select-heading"
            >
              Farbe
            </Heading>

            <ColorSelector
              v-if="accessoriesColors.length > 1"
              circle
              :variants="accessoriesColors"
              class="smartwatch-configurator__additional-color-select"
              :value="selectedAccessoriesColorIndex"
              @select-variant="selectAccessoire"
            />
          </div>
        </template>
      </AccordionItem>
    </div>

    <div
      v-if="hasEntry"
      class="smartwatch-configurator__price"
      role="table"
      aria-label="Ausgewählte Produkte und Preise"
    >
      <div
        class="smartwatch-configurator__price-container smartwatch-configurator__price-container--watch"
        role="row"
      >
        <div role="cell">
          <LinkButton
            tag="nuxt-link"
            text-neutral
            class="text--bold"
            :to="`/geraete/${currentOffer?.product?.slug}`"
          >
            <span class="sr-only">Produktname: </span>
            {{ selectedName }}
          </LinkButton>
        </div>
        <div
          class="smartwatch-configurator__price-content"
          role="cell"
          :aria-label="`Preis für ${selectedName}`"
        >
          <PricesContainer
            :prices-data="currentOffer.prices"
            :variant="selectedAccessoriesColor?.offer?.product ? 'small' : 'medium'"
          />
          <AddToCartButtonContainer
            v-if="!selectedAccessoriesColor?.offer?.product"
            :offer="currentOffer"
            :display-text="false"
          />
        </div>
      </div>
      <div v-if="selectedAccessoriesColor?.offer?.product">
        <div
          class="smartwatch-configurator__price-container"
          role="row"
        >
          <div role="cell">
            <LinkButton
              tag="nuxt-link"
              class="text--bold"
              text-neutral
              :to="`/geraete/${selectedAccessoriesColor.offer.product.slug}`"
            >
              <span class="sr-only">Zusatzprodukt: </span>
              {{ selectedAccessoriesColor.offer.product.name }}
            </LinkButton>
          </div>
          <div
            class="smartwatch-configurator__price-content"
            role="cell"
            :aria-label="`Preis für ${selectedAccessoriesColor.offer.product.name}`"
          >
            <PricesContainer
              variant="small"
              :prices-data="selectedAccessoriesColor.offer.prices"
            />
          </div>
        </div>
        <div
          class="smartwatch-configurator__price-container smartwatch-configurator__price-container--summary"
          role="row"
        >
          <strong
            class="text--h5"
            role="cell"
          >
            Gesamtsumme:
          </strong>
          <div
            class="smartwatch-configurator__price-content"
            role="cell"
            aria-label="Gesamtpreis"
          >
            <PricesContainer
              variant="medium"
              :prices-data="currentOffer.prices"
            />
            <AddToCartButton
              display-icon
              :display-text="false"
              class="add-to-cart-button-container"
              @add-to-cart="addToCartWrapper"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import ImageGalleryContainer from '~/components/product-detail/image-gallery-container.vue';
import { useLazyAsyncData, watch } from '#imports';
import type { Promotion, PromotionOffer } from '~/lib/offers-by-group';
import fetchOffers, { type ListOffer } from '~/lib/goliath/offers';
import type { ListQuery, OfferFilterParams } from '~/lib/goliath/goliath.types';
import SmartwatchSelect, {
  type SelectEntry,
} from '~/components/smartwatch-config/smartwatch-select.vue';
import { getImagesFromOffer, getMainImageFromOffer } from '~/lib/goliath-media';
import PricesContainer from '~/components/product-detail/prices-container.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';
import {
  LinkButton,
  AccordionItem,
  ColorSelector,
  AddToCartButton,
  Heading,
} from '@i22/td-longtail-vue-components';
import type { TransformedProductAlternative, ScopedOffer } from '@i22/rocket';
import { addToCart } from '~/helpers/add-to-cart';

type SmartwatchPromotion = Promotion<{
  articleNumber: string;
  mainImageIndex?: number;
  colorImageIndex?: number;
  connectivity?: string;
  size?: string;
  isPreselected?: boolean;
  active?: boolean;
  color?: string;
}>;

export type SmartwatchPromotionOffer = PromotionOffer<SmartwatchPromotion>;

type AccessoriesPromotion = Promotion<{
  articleNumber: string;
  name: string;
  colorImageIndex?: number;
}>;

export type AccessoriesPromotionOffer = PromotionOffer<AccessoriesPromotion>;

export type SmartwatchGroups = {
  name: string; // Watch 7...
  entries: SmartwatchPromotionOffer[];
};

const props = defineProps({
  smartwatchGroups: {
    type: Array as PropType<SmartwatchGroups[]>,
    default: () => [],
  },
  accessoriesPromotions: {
    type: Array as PropType<AccessoriesPromotion[]>,
    default: () => [],
  },
});

const emits = defineEmits<{
  (e: 'select', articleNumber: string | null): void;
}>();

const selectedGroup = ref<string | null>(null);
const selectedArticleNumber = ref<string | null>(null);
const selectedAccessoire = ref<string | null>(null);

const { data: offers, error: errorData } = useLazyAsyncData(
  'smartwatch-configurator',
  async (): Promise<ListOffer[]> => {
    const articleNumbers = [];
    props.smartwatchGroups.forEach((group: SmartwatchGroups) => {
      group.entries.forEach((entry) => {
        articleNumbers.push(entry.articleNumber);
      });
    });

    if (articleNumbers.length === 0) return [];

    const filter: OfferFilterParams = {
      articleNumbers: Array.from(articleNumbers),
      productAvailable: true,
    };

    const goliathOffers: ListQuery['offers'] | undefined = await fetchOffers(
      { filter, includeAggregations: false },
      false
    );
    return goliathOffers?.entries || [];
  }
);

// get all groups with offers
const groupsWithOffers = computed<SmartwatchGroups[]>(() => {
  if ((offers.value?.length || 0) === 0) return [];

  const target: SmartwatchGroups[] = [];
  props.smartwatchGroups.forEach((group: SmartwatchGroups) => {
    const groupWithEntries: SmartwatchGroups = {
      ...group,
      entries: [],
    };
    group.entries.forEach((entry) => {
      if (!offers.value) return;
      const offer = offers.value.find((o) => o.product?.articleNumber === entry.articleNumber);
      if (offer) {
        groupWithEntries.entries.push({
          ...entry,
          offer,
        });
      }
    });
    if (groupWithEntries.entries.length > 0) {
      target.push(groupWithEntries);
    }
  });
  return target;
});

const smartwatchModels = computed<SelectEntry[]>(() =>
  groupsWithOffers.value.map((group) => ({
    label: group.name,
    value: group.name,
    active: group.name === selectedGroup.value,
  }))
);

// Select first Model
watch(
  [smartwatchModels, selectedGroup],
  () => {
    const isSet = smartwatchModels.value.find((model) => model.value === selectedGroup.value);
    if (isSet) return;
    selectedGroup.value = smartwatchModels.value[0]?.value || null;
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(selectedArticleNumber, () => {
  emits('select', selectedArticleNumber.value);
  selectedAccessoire.value = null;
});

const currentGroup = computed<SmartwatchGroups>(
  () =>
    groupsWithOffers.value.find((group) => group.name === selectedGroup.value) || {
      name: '',
      entries: [],
    }
);

const currentEntry = computed<SmartwatchPromotionOffer | undefined>(() =>
  currentGroup.value.entries.find(
    (entry: SmartwatchPromotionOffer) => selectedArticleNumber.value === entry.articleNumber
  )
);

const currentOffer = computed<ListOffer | undefined>(() => currentEntry.value?.offer);

const offerImages = computed(() =>
  currentOffer.value ? getImagesFromOffer(currentOffer.value) : []
);

const hasEntry = computed(() => !!currentEntry.value?.offer);

const selectedName = computed(() => {
  if (!currentOffer.value) return '';

  return (
    currentOffer.value.productAlternatives?.[0]?.properties?.[0]?.alternatives?.[0]?.offer
      ?.scopedProduct?.model ||
    currentOffer.value.product?.family?.name ||
    currentOffer.value.product?.family?.name
  );
});

// Select articlenumber, if not already set
watch(
  [currentGroup, selectedArticleNumber],
  () => {
    const isSet = currentGroup.value.entries.find(
      (entry) => entry.articleNumber === selectedArticleNumber.value
    );
    if (isSet) return;
    const isPreSelected = currentGroup.value.entries.find((entry) => entry.isPreselected);

    const preselectedEntry: SmartwatchPromotionOffer | null =
      isPreSelected || currentGroup.value.entries[0] || null;
    if (preselectedEntry) {
      selectedArticleNumber.value = preselectedEntry.articleNumber;
    } else {
      selectedArticleNumber.value = null;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

const getBestMatchingEntry = (
  referenceEntry: SmartwatchPromotionOffer,
  isColor = false
): SmartwatchPromotionOffer => {
  let matchingEntry: SmartwatchPromotionOffer | undefined = currentGroup.value.entries.find(
    (e) =>
      e.connectivity === referenceEntry.connectivity &&
      e.size === referenceEntry.size &&
      e.color === referenceEntry.color &&
      e.articleNumber !== referenceEntry.articleNumber
  );

  if (!matchingEntry) {
    matchingEntry = currentGroup.value.entries.find(
      (e) =>
        e.connectivity === referenceEntry.connectivity &&
        e.size === referenceEntry.size &&
        isColor &&
        e.color === referenceEntry.color &&
        e.articleNumber !== referenceEntry.articleNumber
    );
  }

  return matchingEntry || referenceEntry;
};

const smartwatchConnectivity = computed<SelectEntry[]>(() => {
  const variants: SelectEntry[] = [];
  currentGroup.value.entries.forEach((entry: SmartwatchPromotionOffer) => {
    if (!entry.connectivity) return;

    // show only entries with matching size
    if (currentEntry.value?.size && currentEntry.value.size !== entry.size) return;

    // if entry already exists, just mark is.
    const isPresent = variants.find(({ label }) => label === entry.connectivity);
    if (isPresent) {
      if (entry.connectivity === currentEntry.value?.connectivity) {
        isPresent.active = true;
      }
      return;
    }

    variants.push({
      label: entry.connectivity,
      value: getBestMatchingEntry(entry)?.articleNumber || '',
      active: entry.connectivity === currentEntry.value?.connectivity,
    });
  });
  return variants;
});

const smartwatchSize = computed<SelectEntry[]>(() => {
  const variants: SelectEntry[] = [];
  currentGroup.value.entries.forEach((entry: SmartwatchPromotionOffer) => {
    if (!entry.size) return;

    // show only entries with matching connectivity
    if (currentEntry.value?.connectivity && currentEntry.value.connectivity !== entry.connectivity)
      return;

    // if entry already exists, just mark is.
    const isPresent = variants.find(({ label }) => label === entry.size);
    if (isPresent) {
      if (entry.size === currentEntry.value?.size) {
        isPresent.active = true;
      }
      return;
    }

    variants.push({
      label: entry.size,
      value: getBestMatchingEntry(entry)?.articleNumber || '',
      active: entry.size === currentEntry.value?.size,
    });
  });
  return variants;
});

const smartwatchColors = computed(() => {
  const variants: SelectEntry[] = [];
  currentGroup.value.entries.forEach((entry: SmartwatchPromotionOffer) => {
    if (!entry.color) return;

    // show only entries with matching size
    if (currentEntry.value?.size && currentEntry.value.size !== entry.size) return;
    // show only entries with matching connectivity
    if (currentEntry.value?.connectivity && currentEntry.value.connectivity !== entry.connectivity)
      return;

    // if entry already exists, just mark is.
    const isPresent = variants.find(({ label }) => label === entry.color);
    if (isPresent) {
      if (entry.color === currentEntry.value?.color) {
        isPresent.active = true;
      }
      return;
    }

    const bestMatchingEntry = getBestMatchingEntry(entry, true);
    const image = getMainImageFromOffer(
      bestMatchingEntry.offer,
      bestMatchingEntry.colorImageIndex || 0
    )?.small;
    variants.push({
      label: entry.color,
      value: bestMatchingEntry.articleNumber,
      image,
      active: entry.color === currentEntry.value?.color,
    });
  });
  return variants;
});

/* Accessories */

const { data: accessories } = useLazyAsyncData(
  'smartwatch-configurator-accessories',
  async (): Promise<ListOffer[]> => {
    const articleNumbers = props.accessoriesPromotions.map(
      (promotion: AccessoriesPromotion) => promotion.articleNumber
    );
    if (articleNumbers.length === 0) return [];

    const filter: OfferFilterParams = {
      articleNumbers: Array.from(articleNumbers),
      productAvailable: true,
    };

    const goliathOffers: ListQuery['offers'] | undefined = await fetchOffers(
      { filter, includeAggregations: false },
      false
    );
    return goliathOffers?.entries || [];
  },
  {
    watch: [() => props.accessoriesPromotions],
  }
);

const accessoriesWithOffers = computed<AccessoriesPromotionOffer[]>(() => {
  if ((accessories.value?.length || 0) === 0) return [];

  const target: AccessoriesPromotionOffer[] = [];
  props.accessoriesPromotions.forEach((promotion: AccessoriesPromotion) => {
    const offer = accessories.value.find(
      (o: ListOffer) => o.product?.articleNumber === promotion.articleNumber
    );
    if (offer) {
      target.push({
        ...promotion,
        offer,
      });
    }
  });
  return target;
});

const isOfferOrAlternativeSelected = (
  offer: ListOffer | undefined,
  articleNumber: string | null
) => {
  if (!offer?.product || !articleNumber) return false;
  if (offer.product.articleNumber === articleNumber) return true;
  if (
    offer.productAlternatives &&
    offer.productAlternatives.find((productAlternative) => {
      if (!productAlternative?.properties) return false;

      // @ts-ignore
      return productAlternative.properties.find((property) =>
        property?.alternatives?.find(
          (alternative) => alternative?.offer?.product?.articleNumber === articleNumber
        )
      );
    })
  ) {
    return true;
  }
  return false;
};

const accessoriesList = computed(() => {
  if (!accessoriesWithOffers.value) return [];
  const variants: SelectEntry[] = accessoriesWithOffers.value.map(
    (accessoireEntry: AccessoriesPromotionOffer) => ({
      label: accessoireEntry.name,
      value: accessoireEntry.articleNumber,
      active: isOfferOrAlternativeSelected(accessoireEntry.offer, selectedAccessoire.value),
      image: getMainImageFromOffer(accessoireEntry.offer, accessoireEntry.colorImageIndex || 0)
        ?.small,
    })
  );
  return variants;
});

const accessoriesColors = computed<TransformedProductAlternative<ScopedOffer>[]>(() => {
  const currentAccessoire: AccessoriesPromotionOffer | undefined = accessoriesWithOffers.value.find(
    (accessoireEntry: AccessoriesPromotionOffer) =>
      isOfferOrAlternativeSelected(accessoireEntry.offer, selectedAccessoire.value)
  );

  if (!currentAccessoire?.offer?.productAlternatives) return [];

  const variants: TransformedProductAlternative<ScopedOffer>[] = [];
  currentAccessoire.offer.productAlternatives.forEach((alternativeGroup) => {
    alternativeGroup?.properties?.forEach((property) => {
      if (property?.key !== 'COLOR') return;
      property.alternatives?.forEach((alternative) => {
        if (!alternative?.offer?.scopedProduct || !alternative.offer.product?.articleNumber) return;

        // @ts-ignore
        variants.push({
          active: alternative.offer.product.articleNumber === selectedAccessoire.value,
          offer: alternative.offer,
          name: alternative.offer.scopedProduct.color?.name,
          color: alternative.offer.scopedProduct.color,
          articleNumber: alternative.offer.product.articleNumber,
          teaserImage: getMainImageFromOffer(alternative.offer),
        });
      });
    });
  });
  return variants;
});

const selectedAccessoriesColorIndex = computed(() =>
  accessoriesColors.value.findIndex((entry) => entry.articleNumber === selectedAccessoire.value)
);

const selectedAccessoriesColor = computed(() =>
  accessoriesColors.value.find((entry) => entry.articleNumber === selectedAccessoire.value)
);

const selectAccessoire = (val: string | TransformedProductAlternative<ScopedOffer>) => {
  const articleNumber = val?.variant?.offer?.product?.articleNumber || val;
  if (selectedAccessoire.value === articleNumber) {
    selectedAccessoire.value = null;
    return;
  }
  selectedAccessoire.value = articleNumber;
};

const addToCartWrapper = () => {
  if (!currentOffer.value) return;
  const items = [currentOffer.value];
  if (selectedAccessoriesColor.value) {
    items.push(selectedAccessoriesColor.value.offer);
  }
  addToCart(items);
};

// Update pagination from Draggable Scroller on Open
const selectAccessoireElement = ref(null);
const openAccordion = () => {
  setTimeout(() => {
    if (!selectAccessoireElement.value) return;
    selectAccessoireElement.value.updateScroller();
  }, 1);
};
</script>

<style lang="scss">
@use 'assets/base' as *;

.smartwatch-configurator {
  display: flex;
  grid-gap: $grid-base;
  grid-template-columns: 100%;
  flex-wrap: wrap;
  padding-block: $grid-base;

  @include mq($mq-sm) {
    grid-template-columns: 1fr 1fr;
    grid-gap: $grid-base * 2;
    padding-block: $grid-base * 2;
    grid-template-areas: 'image configurator' 'price additional' 'color color';
  }

  &__image,
  &__variant,
  &__additional,
  &__price {
    background: var(--color-bg);
    padding: $grid-base;
    flex: 0 0 100%;
    flex-wrap: wrap;
    width: 100%;

    @include mq($mq-sm) {
      flex: 0 0 calc(50% - $grid-base);
      width: calc(50% - $grid-base);
    }
  }

  &__image-gallery {
    .image-gallery__stage {
      margin: 0;
      padding: 0;
      @include mq($mq-sm) {
        aspect-ratio: auto;
      }
    }

    .image-gallery__image {
      @include mq($mq-sm) {
        max-height: 520px;
      }
    }
  }

  &__variant {
    display: grid;
    grid-template-areas: 'model model' 'connectivity size' 'color color';
    grid-template-columns: 50% 50%;
    grid-gap: $grid-base;

    @include mq($mq-sm) {
      align-content: space-between;
    }

    @include mq($mq-md) {
      grid-gap: $grid-base * 4 $grid-base * 2;
    }
  }

  &__model-select {
    grid-area: model;
  }
  &__connectivity-select {
    grid-area: connectivity;
  }
  &__size-select {
    grid-area: size;
  }
  &__color-select {
    grid-area: color;
  }

  &__additional {
    @include mq($mq-sm) {
      order: 4;
    }

    &-armband {
      margin-bottom: $grid-base;
    }

    &-color-select {
      margin-bottom: $grid-base;
    }
  }

  &__select-heading.heading {
    margin-bottom: $grid-base;
  }

  &__price {
    display: flex;
    flex-direction: column;
    justify-content: end;
    align-self: end;

    &-container {
      display: flex;
      grid-gap: $grid-base;
      justify-content: space-between;
      align-items: center;

      &--summary {
        border-top: 1px solid var(--color-stroke);
        padding-top: $grid-base;
        margin-top: $grid-base;
      }

      &--watch {
        margin-bottom: $grid-base;
      }
    }

    &-content {
      grid-gap: $grid-base;
      display: flex;
      align-items: center;
    }

    .link-button {
      white-space: normal;
      text-align: left;
    }
  }
}
</style>
